import axios from 'axios';
import { register } from 'register-service-worker';
import { clearOfflineData, pruneOfflineData, sendOutbox } from '@/offline/offlineStore.js';

export default {
  install(app) {

// Report dates: the date boxes give the user's own local time, but the database saves UTC
// (getCurrentDateTime() in PHP). Before any request leaves, startDate / endDate are changed
// from local time to UTC "Y-m-d H:i:s". Values already in UTC (ending in "Z") are left alone.
const reportDateToUtc = (value, isEndDate) => {
   if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return value;

   const date = new Date(value); // a value with no "Z" is read as the user's local time
   if (isNaN(date.getTime())) return value;

   // "23:59" becomes "23:59:59" so the last minute of the day is included
   if (isEndDate && value.length === 16) date.setSeconds(59);

   return date.toISOString().slice(0, 19).replace('T', ' ');
};

axios.interceptors.request.use((config) => {
   // Dates in the URL (fetchRecords, goToLastPage)
   if (config.url) {
      config.url = config.url.replace(/([?&])(startDate|endDate)=([^&]*)/g, (match, separator, key, value) => {
         const utc = reportDateToUtc(value, key === 'endDate');
         return utc === value ? match : `${separator}${key}=${encodeURIComponent(utc)}`;
      });
   }

   // Dates in FormData (exportRecords)
   if (config.data instanceof FormData) {
      ['startDate', 'endDate'].forEach((key) => {
         const value = config.data.get(key);
         if (value) config.data.set(key, reportDateToUtc(value, key === 'endDate'));
      });
   }

   return config;
});

// Every API response (moved from main.js):
//  - after a company/branch switch: load the new company's permissions
//  - after login / MFA login / switch: remember if the active company is the platform company
//  - plan missing, expired, or doesn't include the feature: open My Subscription (works for every page)
// $store and $router are read when a response arrives, so the order of app.use() in main.js doesn't matter
let redirectingToSubscription = false;
axios.interceptors.response.use((response) => {
    const store = app.config.globalProperties.$store;
    const router = app.config.globalProperties.$router;
    const sentData = response.config?.data;


    if (
        sentData instanceof FormData &&
        sentData.get("action") === "switchCompanyBranch" &&
        response.data?.status === "success" &&
        response.data.results?.permissions
    ) {
        const r = response.data.results;
        localStorage.setItem("roles", JSON.stringify(r.roles));
        localStorage.setItem("permissions", JSON.stringify(r.permissions));
        localStorage.setItem("subscription", JSON.stringify(r.subscription));
        localStorage.setItem("subscription_permissions", JSON.stringify(r.subscription_permissions));
        store.commit("SET_PERMISSIONS", r.permissions);
    }

    if (
        sentData instanceof FormData &&
        ["login", "mfaVerifyCode", "switchCompanyBranch"].includes(sentData.get("action")) &&
        response.data?.status === "success" &&
        response.data.results?.is_platform !== undefined
    ) {
        store.commit("SET_IS_PLATFORM", response.data.results.is_platform);
    }

    // After login / MFA login / switch: the active company's currency for money boxes (KES / USD)
    if (
        sentData instanceof FormData &&
        ["login", "mfaVerifyCode", "switchCompanyBranch"].includes(sentData.get("action")) &&
        response.data?.status === "success" &&
        response.data.results?.company_currency
    ) {
        localStorage.setItem("company_currency", response.data.results.company_currency);
    }

    if (response.data?.code === "SUBSCRIPTION_REQUIRED" && !redirectingToSubscription) {
        redirectingToSubscription = true;
        router
            .push({ path: "/profile/my_subscription/", query: { notice: response.data.message } })
            .finally(() => {
                redirectingToSubscription = false;
            });
    }
    return response;
});

/* Sounds (moved from main.js). Switched on/off with soundsEnabled in main.js ($soundsEnabled) */
const soundsOn = () => app.config.globalProperties.$soundsEnabled === true;

const typingSound = new Audio('/sounds/typing.wav');
const buttonSound = new Audio('/sounds/button.mp3');
const focusSound = new Audio('/sounds/focus.mp3');
const errorSound = new Audio('/sounds/error.wav');

typingSound.preload = 'auto';
buttonSound.preload = 'auto';
focusSound.preload = 'auto';
errorSound.preload = 'auto';

// Play the error sound (used when a form shows an error alert).
app.config.globalProperties.$playErrorSound = function () {
    if (!soundsOn()) return;
    errorSound.pause();
    errorSound.currentTime = 0;
    errorSound.play().catch(() => {});
};

document.addEventListener('keydown', (event) => {
   if (!soundsOn()) return;

   // No typing sound for Enter in single-line inputs (it submits, it doesn't type).
   if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') return;

   if (
      event.target.tagName === 'INPUT' ||
      event.target.tagName === 'TEXTAREA' ||
      event.target.isContentEditable
   ) {
      typingSound.pause();
      typingSound.currentTime = 0;
      typingSound.play().catch(() => {});
   }
});

document.addEventListener('click', (event) => {
   if (!soundsOn()) return;

   if (event.target.closest('button') || event.target.closest('a')) {
      buttonSound.pause();
      buttonSound.currentTime = 0;
      buttonSound.play().catch(() => {});
   }
});

document.addEventListener('focusin', (event) => {
   if (!soundsOn()) return;

   if (
      event.target.tagName === 'INPUT' ||
      event.target.tagName === 'TEXTAREA' ||
      event.target.isContentEditable ||
      event.target.tagName === 'SELECT'
   ) {
      focusSound.pause();
      focusSound.currentTime = 0;
      focusSound.play().catch(() => {});
   }
});
/* End Sounds */

/*
 * Offline (Phase 1): the app opens without internet.
 * Called once from main.js: this.$setupOffline(true / false)  (VUE_APP_OFFLINE_ENABLED in the Vue .env)
 */
// Sidebar: a menu name cut off with "…" shows its full name on hover (names that fit get no tooltip)
document.addEventListener('mouseover', (e) => {
   const link = e.target.closest && e.target.closest('#sidebar .sa-tree .nav-link');
   if (!link) return;
   const cutOff = link.scrollWidth > link.clientWidth;
   if (cutOff) {
      link.title = link.textContent.trim().replace(/\s+/g, ' ');
   } else {
      link.removeAttribute('title');
   }
});



// Row ⋮ / Actions menus: while open, the menu is moved onto the page itself, so the table box
// (side scroll) can't cut it off and the glass blur can't push it out of place. Put back when closed.
document.addEventListener('show.bs.dropdown', (e) => {
   const btn = e.target;
   if (!btn.classList || !btn.classList.contains('row-actions-btn')) return;
   const menu = btn.parentElement && btn.parentElement.querySelector('.dropdown-menu');
   if (!menu) return;
   btn._rowMenu = menu;
   btn._rowMenuHome = menu.parentElement;
   document.body.appendChild(menu);
});
document.addEventListener('hidden.bs.dropdown', (e) => {
   const btn = e.target;
   const menu = btn && btn._rowMenu;
   if (!menu) return;
   // Back into its row (or removed, if the row is gone, e.g. the table reloaded)
   if (btn._rowMenuHome && btn._rowMenuHome.isConnected) btn._rowMenuHome.appendChild(menu);
   else menu.remove();
   btn._rowMenu = null;
   btn._rowMenuHome = null;
});

// Small message bar at the top or bottom of the screen ("You're offline", "New version available")
// position: 'bottom' (centred) or 'bottom-left'. icon: Font Awesome classes. title: text shown on hover
// Messages at the bottom of the screen as Bootstrap alerts (stacked when there are several)
// type: Bootstrap colour (warning, info, success, danger). icon: Font Awesome classes
// buttonText: when given, the bar gets a real button (runs onClick) and an ✕ to close it
const showAppBanner = (id, text, type = 'info', onClick = null, icon = '', buttonText = '') => {
   let box = document.getElementById('app-banners');
   if (!box) {
      box = document.createElement('div');
      box.id = 'app-banners';
      box.style.cssText = 'position:fixed;left:50%;bottom:calc(40px + env(safe-area-inset-bottom));transform:translateX(-50%);z-index:2000;'
         + 'width:min(640px, calc(100% - 32px));display:flex;flex-direction:column;gap:8px;';
      document.body.appendChild(box);
   }
   let bar = document.getElementById(id);
   if (!bar) {
      bar = document.createElement('div');
      bar.id = id;
      bar.setAttribute('role', 'status');
      // Whole bar is clickable only when there is no button
      if (onClick && !buttonText) {
         bar.style.cursor = 'pointer';
         bar.addEventListener('click', onClick);
      }
      box.appendChild(bar);
   }
   bar.className = buttonText
      ? 'alert app-banner-action shadow-lg d-flex align-items-center gap-2 mb-0 py-2 px-3'
      : `alert alert-${type} shadow d-flex align-items-center gap-2 mb-0 py-2 px-3`;
   bar.innerHTML = '';
   if (icon) {
      const i = document.createElement('i');
      i.className = icon;
      bar.appendChild(i);
   }
   const span = document.createElement('span');
   span.textContent = text;
   if (buttonText) span.className = 'flex-grow-1 fw-semibold';
   bar.appendChild(span);

   if (buttonText) {
      // The button (e.g. "Reload")
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn btn-sm app-banner-btn';
      btn.textContent = buttonText;
      btn.addEventListener('click', () => {
         btn.disabled = true;
         btn.innerHTML = '<i class="fa fa-refresh fa-spin me-1"></i>' + buttonText;
         if (onClick) onClick();
      });
      bar.appendChild(btn);

      // ✕ to close the message (it comes back next time the app opens)
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'app-banner-close';
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<i class="fa fa-times"></i>';
      close.addEventListener('click', () => hideAppBanner(id));
      bar.appendChild(close);
   }
};


const hideAppBanner = (id) => document.getElementById(id)?.remove();

// Offline (Phase 2): when a table shows saved rows, the offline bar says from when
let offlineSavedAt = null;
const formatSavedAt = (ms) => {
   const d = new Date(ms);
   const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
   if (d.toDateString() === new Date().toDateString()) return `at ${time}`;
   return `on ${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} at ${time}`;
};

const offlineBarText = () => offlineSavedAt
   ? `You're offline. Showing data saved ${formatSavedAt(offlineSavedAt)}. Changes you save are kept and sent when the internet is back.`
   : "You're offline. Changes you save are kept and sent when the internet is back.";
// The offline message: a yellow Bootstrap alert at the bottom
const showOfflinePill = () => showAppBanner('app-offline-banner', offlineBarText(), 'warning', null, 'fa fa-wifi');
// Called by the table after every load: savedAt = time of the saved copy, or null for fresh data
app.config.globalProperties.$offlineDataBanner = function (savedAt) {
   offlineSavedAt = savedAt || null;
   if (offlineSavedAt || !navigator.onLine) {
      showOfflinePill();
   } else {
      hideAppBanner('app-offline-banner');
   }
};

// Offline (Phase 3): sends the saves made offline, oldest first. Safe to call often.
let outboxSyncing = false;
app.config.globalProperties.$syncOutbox = async function () {
   const g = app.config.globalProperties;
   if (outboxSyncing || !navigator.onLine || !g.$offlineEnabled) return;
   outboxSyncing = true;
   try {
      const { sent, failed, loginExpired } = await sendOutbox(g.$handleConfig());
      if (!sent && !failed && !loginExpired) return;
      // Table pages reload when they hear this
      if (sent || failed) window.dispatchEvent(new CustomEvent('outbox-synced', { detail: { sent, failed } }));
      const text = (sent ? `${sent} offline save(s) sent. ` : '')
         + (failed ? `${failed} couldn't be sent - look for "Not sent" in the table. ` : '')
         // Login ran out while offline: the rest wait until the user logs in again
         + (loginExpired ? 'Your login has expired. Log in again and your offline saves will be sent.' : '');
      showAppBanner('app-sync-banner', text, failed || loginExpired ? 'warning' : 'success',
         () => hideAppBanner('app-sync-banner'),
         failed || loginExpired ? 'fa fa-exclamation-circle' : 'fa fa-check-circle');
      setTimeout(() => hideAppBanner('app-sync-banner'), loginExpired ? 10000 : 6000);
   } catch (e) {
      console.error('Sending offline saves failed:', e);
   } finally {
      outboxSyncing = false;
   }
};

app.config.globalProperties.$setupOffline = function (enabled) {
   // Saved tables: removed when offline is switched off, old ones (14+ days) tidied when on
   if (enabled) pruneOfflineData(); else clearOfflineData();

   if (!('serviceWorker' in navigator)) return;

   if (!enabled) {
      // Switched off: remove any offline copy already on this device, so an old copy never keeps running
      navigator.serviceWorker.getRegistrations().then((registrations) => registrations.forEach((r) => r.unregister()));
      if (window.caches) {
         caches.keys().then((keys) => keys.forEach((key) => caches.delete(key)));
      }
      return;
   }

   // "You're offline" bar while there is no internet (also during npm run serve)
   const updateOnlineBar = () => {
      if (navigator.onLine) {
         offlineSavedAt = null;
         hideAppBanner('app-offline-banner');
      } else {
         showOfflinePill();
      }
   };
   window.addEventListener('online', updateOnlineBar);
   window.addEventListener('offline', updateOnlineBar);
   updateOnlineBar();

   // Offline saves: send them when the internet comes back, and once now
   window.addEventListener('online', () => this.$syncOutbox());
   this.$syncOutbox();

   // Only in a built app (npm run build). npm run serve keeps working online as before.
   if (process.env.NODE_ENV !== 'production') return;

   register(`${process.env.BASE_URL}service-worker.js`, {
      // A new version was uploaded: offer to load it
      updated(registration) {
         showAppBanner('app-update-banner', 'New version available', 'info', () => {
            // Ask the new version to take over (the page then reloads by itself).
            // If it isn't waiting any more, just reload.
            if (registration.waiting) registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            else window.location.reload();
         }, 'fa fa-refresh', 'Reload');
      },
      error(error) {
         console.error('Offline setup failed:', error);
      },
   });

   // The new version has taken over: load it once
   let reloading = false;
   navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
   });


};
/* End Offline */


// Colour of the phone's top bar (status bar).
// Called once from main.js, and from any component: this.$setStatusBarColor('#198754')
app.config.globalProperties.$setStatusBarColor = function (color) {
   let meta = document.querySelector('meta[name="theme-color"]');
   if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
   }
   meta.setAttribute('content', color);
};

// MFA login expired or cancelled: clear it and go back to login with the message
app.config.globalProperties.$mfaRestart = function (message) {
    sessionStorage.removeItem('mfa_token');
    sessionStorage.removeItem('mfa_phone');
    sessionStorage.removeItem('mfa_email');
    localStorage.removeItem('expiry_seconds');
    sessionStorage.setItem('backend_message', message || 'Please log in again.');
    sessionStorage.setItem('backend_alertType', 'danger');
    this.$router.push('/');
};

// ✅ Global validation clear function
app.config.globalProperties.$clearValidationStates = function (formId) {
    setTimeout(() => {
        const form = document.querySelector(formId);
        if (!form) return;

        const fields = form.querySelectorAll('input, select, textarea, .validation-field, .multiSelect-validation-field');

        fields.forEach(field => {
            field.classList.remove('is-invalid', 'is-valid');

            const feedback = field.nextElementSibling;
            if (feedback && feedback.classList.contains('invalid-feedback')) {
                feedback.style.display = 'none';
            }
        });
    }, 0);
};
// ✅ End Global validation clear function

app.config.globalProperties.$toggleRecordDetails = function (recordId) {
    const currentElement = document.getElementById('tableDetails' + recordId);

    if (!currentElement) return;

    document.querySelectorAll('#bootstrapTable .table-details.show').forEach((element) => {
        if (element !== currentElement) {
            bootstrap.Collapse.getOrCreateInstance(element, {
                toggle: false
            }).hide();
        }
    });

    bootstrap.Collapse.getOrCreateInstance(currentElement, {
        toggle: false
    }).toggle();
};

// ✅ Global Hide Modal
app.config.globalProperties.$hideModal = (modalId) => {
   const modalElement = document.getElementById(modalId)
   if (!modalElement) return

   const modalInstance =
      bootstrap.Modal.getInstance(modalElement) ||
      new bootstrap.Modal(modalElement)

   modalInstance.hide()
}
// ✅ End Global Hide Modal


// ✅ Optional: Global Show Modal
app.config.globalProperties.$showModal = (modalId) => {
   const modalElement = document.getElementById(modalId)
   if (!modalElement) return

   const modalInstance =
      bootstrap.Modal.getInstance(modalElement) ||
      new bootstrap.Modal(modalElement)

   modalInstance.show()
}    
// ✅ End Optional: Global Show Modal


// Company currency for money boxes: KES for Kenyan companies, USD for others (same rule as billing).
// Saved at login and company switch as localStorage "company_currency".
app.config.globalProperties.$companyCurrency = function () {
   try {
      return (localStorage.getItem('company_currency') || 'KES').toUpperCase();
   } catch (e) {
      return 'KES';
   }
};

// 1250.5 -> "KES 1,250.50" (tables, totals, details)
app.config.globalProperties.$formatMoney = function (amount, currency = null, decimals = 2) {
   if (amount === null || amount === undefined || amount === '') return '';
   const number = Number(String(amount).replace(/,/g, ''));
   if (!Number.isFinite(number)) return String(amount);
   const code = currency || app.config.globalProperties.$companyCurrency();
   return code + ' ' + number.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

    
/*v-select & hidden select validation*/
    app.config.globalProperties.$isValid = function (field) {
      const value = this.formInputs[field];
      // multiselect (array)
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      // single value
      return value !== null && value !== undefined && value !== '';
    };
    app.config.globalProperties.$isInvalid = function (field) {
      return this.submitted && !this.$isValid(field);
    };
/*End v-select & hidden select validation*/


/*Form validations- for validateField
  Every rule is an attribute on the field, so FormField, SelectField, CheckField and RadioField pass them on.
    required                      must be filled in (ticked / chosen for checkbox, radio, file)
    type="email" | "url" | "tel" | "number" | "date" | "time" ...   the format of that type
    minlength="2" / maxlength="50"   number of characters
    min / max                     numbers, money and dates (dates can also use "today")
    step="0.5"                    numbers: only steps of 0.5
    pattern="..."                 your own format, e.g. pattern="[A-Z]\d{9}[A-Z]" (KRA PIN)
    data-type="money"             an amount: 1250 / 1,250.50   (data-decimals="2", data-allow-negative)
    data-type="integer"           a whole number                (data-allow-negative)
    data-phone="ke"               Kenyan phone: 07.., 01.., +254..   (default: any country, 7-15 digits)
    accept="image/*,.pdf"         file types allowed;   data-max-size="5" = up to 5 MB per file
    data-match="password"         must be the same as the field with that name or id (confirm password)
    data-validate="off"           never check this field
  Optional fields: left empty = no colour; filled in = checked and coloured like required ones.
*/
const saValidation = {
    // Today as YYYY-MM-DD in the user's own time zone
    today() {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    // Green / red (or no colour for an optional field left empty)
    setState(input, valid, showColour) {
        if (!showColour) {
            input.classList.remove("is-valid", "is-invalid");
        } else if (valid) {
            input.classList.add("is-valid");
            input.classList.remove("is-invalid");
        } else {
            input.classList.add("is-invalid");
            input.classList.remove("is-valid");
        }
    },
    // Does the file match accept="image/*,.pdf,application/pdf"?
    fileMatchesAccept(file, accepted) {
        const name = (file.name || "").toLowerCase();
        const mime = (file.type || "").toLowerCase();
        return accepted.some((rule) => {
            if (rule.startsWith(".")) return name.endsWith(rule);
            if (rule.endsWith("/*")) return mime.startsWith(rule.slice(0, -1));
            return mime === rule;
        });
    },
    // Amounts: "1,250.50" -> 1250.5. Not a valid amount -> null
    parseMoney(value) {
        const cleaned = String(value ?? "").replace(/,/g, "").replace(/\s/g, "").trim();
        if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
        return Number(cleaned);
    },
    // Is "value" a whole number of "step"s away from "base"? (e.g. step 0.5 from 0)
    fitsStep(number, step, base) {
        const steps = (number - base) / step;
        return Math.abs(steps - Math.round(steps)) < 1e-9;
    },
};

app.config.globalProperties.$validateField = function (input) {
    if (!input) return false;

    // Skipped: switched off, disabled, or the typing box inside a dropdown (v-select)
    if (input.disabled || input.dataset?.validate === "off" || input.classList.contains("vs__search")) {
        return true;
    }

    const type = (input.type || input.tagName || "").toLowerCase();
    const required = input.hasAttribute("required");
    const attr = (name) => input.getAttribute(name);
    let valid = true;

    // ---- Checkbox: required = must be ticked ----
    if (type === "checkbox") {
        if (!required) return true;
        valid = input.checked;
        saValidation.setState(input, valid, true);
        return valid;
    }

    // ---- Radio: required = one of the group must be chosen ----
    if (type === "radio") {
        if (!required) return true;
        const radios = document.querySelectorAll(`input[name="${input.name}"]`);
        valid = !!document.querySelector(`input[name="${input.name}"]:checked`);

        // The buttons' outline turns red / back to normal
        radios.forEach((radio) => {
            const label = document.querySelector(`label[for="${radio.id}"]`);
            if (!label) return;
            label.classList.toggle("btn-outline-danger", !valid);
            label.classList.toggle("btn-outline-primary", valid);
        });
        saValidation.setState(input, valid, true);
        return valid;
    }

    // ---- File: required, accept="..." types, data-max-size="5" (MB) ----
    if (type === "file") {
        const files = Array.from(input.files || []);
        const filled = files.length > 0;
        if (required && !filled) valid = false;

        if (filled) {
            const accepted = (attr("accept") || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
            const maxMb = parseFloat(input.dataset?.maxSize);
            files.forEach((file) => {
                if (accepted.length && !saValidation.fileMatchesAccept(file, accepted)) valid = false;
                if (maxMb > 0 && file.size > maxMb * 1024 * 1024) valid = false;
            });
        }
        saValidation.setState(input, valid, required || filled);
        return valid;
    }

    // ---- Everything else: text, email, url, tel, number, money, date, select, textarea, hidden ----
    const value = String(input.value ?? "").trim();
    const filled = value !== "";

    // Hidden inputs (SelectField's dropdowns): only "required"
    if (type === "hidden") {
        if (!required) return true;
        valid = filled;
        saValidation.setState(input, valid, true);
        return valid;
    }

    if (!filled) {
        valid = !required; // empty: wrong only when required
    } else {
        // Length
        const minLength = parseInt(attr("minlength"), 10);
        const maxLength = parseInt(attr("maxlength"), 10);
        if (minLength > 0 && value.length < minLength) valid = false;
        if (maxLength > 0 && value.length > maxLength) valid = false;

        const dataType = (input.dataset?.type || "").toLowerCase();
        const allowNegative = input.hasAttribute("data-allow-negative");
        const min = attr("min");
        const max = attr("max");

        // Money: 1250, 1,250.50 ... (data-decimals = most digits after the dot, default 2)
        if (dataType === "money") {
            const amount = saValidation.parseMoney(value);
            const decimals = input.dataset?.decimals !== undefined ? parseInt(input.dataset.decimals, 10) : 2;
            const fraction = value.split(".")[1] || "";
            if (amount === null) valid = false;
            else {
                if (!allowNegative && amount < 0) valid = false;
                if (fraction.length > decimals) valid = false;
                if (min !== null && min !== "" && amount < Number(min)) valid = false;
                if (max !== null && max !== "" && amount > Number(max)) valid = false;
            }
        }

        // Whole numbers
        else if (dataType === "integer") {
            const pattern = allowNegative ? /^-?\d+$/ : /^\d+$/;
            if (!pattern.test(value.replace(/,/g, ""))) valid = false;
            else {
                const number = Number(value.replace(/,/g, ""));
                if (min !== null && min !== "" && number < Number(min)) valid = false;
                if (max !== null && max !== "" && number > Number(max)) valid = false;
            }
        }

        // Email
        else if (type === "email") {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) valid = false;
        }

        // Website: https://example.com, example.com and www.example.com are all fine
        else if (type === "url") {
            try {
                const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
                if (!/^https?:$/.test(url.protocol) || !url.hostname.includes(".")) valid = false;
            } catch (e) {
                valid = false;
            }
        }

        // Phone: data-phone="ke" = Kenyan; otherwise any country (7-15 digits, + allowed in front)
        else if (type === "tel") {
            const digits = value.replace(/[\s\-().]/g, "");
            const kenyan = (input.dataset?.phone || "").toLowerCase() === "ke";
            const pattern = kenyan ? /^(?:\+?254|0)(?:7|1)\d{8}$/ : /^\+?\d{7,15}$/;
            if (!pattern.test(digits)) valid = false;
        }

        // Numbers: min, max, step
        else if (type === "number" || type === "range") {
            const number = Number(value);
            if (!Number.isFinite(number)) valid = false;
            else {
                if (min !== null && min !== "" && number < Number(min)) valid = false;
                if (max !== null && max !== "" && number > Number(max)) valid = false;
                const step = attr("step");
                if (step && step !== "any" && Number(step) > 0) {
                    const base = min !== null && min !== "" ? Number(min) : 0;
                    if (!saValidation.fitsStep(number, Number(step), base)) valid = false;
                }
            }
        }

        // Dates and times: the browser gives YYYY-MM-DD (or HH:MM ...), so text order = date order
        else if (["date", "time", "datetime-local", "month", "week"].includes(type)) {
            if (input.validity && input.validity.badInput) valid = false;
            const limit = (v) => (v === "today" && type === "date" ? saValidation.today() : v);
            if (min) {
                const m = limit(min);
                if (value < m) valid = false;
            }
            if (max) {
                const m = limit(max);
                if (value > m) valid = false;
            }
        }

        // Your own format: pattern="..." (the whole value must match)
        const pattern = attr("pattern");
        if (pattern) {
            try {
                if (!new RegExp(`^(?:${pattern})$`).test(value)) valid = false;
            } catch (e) {
                // a broken pattern is ignored
            }
        }

        // Must be the same as another field (confirm password)
        const match = input.dataset?.match;
        if (match) {
            const scope = input.form || document;
            const other = scope.querySelector(`[name="${match}"]`) || document.getElementById(match);
            if (other && String(other.value ?? "") !== String(input.value ?? "")) valid = false;
        }
    }

    // Colour: required fields always; optional ones only when filled in
    saValidation.setState(input, valid, required || filled);
    return valid;
};

// Amounts typed with commas, e.g. "1,250.50" -> 1250.5 (null if it isn't an amount).
// Use it before sending: formData.append("amount", this.$parseMoney(this.formInputs.amount));
app.config.globalProperties.$parseMoney = function (value) {
    return saValidation.parseMoney(value);
};
/*End Form validations- for validateField*/


    /*Form validations- for validateForm */
    app.config.globalProperties.$validateForm = function (formSelector) {
    const form = document.querySelector(formSelector);

    if (!form) return false;

    let isValid = true;

    // Validate all input elements
    form.querySelectorAll('input').forEach(input => {
      //console.log("Input detected:", input.name, input.type, input.value);
        if (!this.$validateField(input)) {
            isValid = false;
        }
    });

    // Validate all textarea elements
    form.querySelectorAll('textarea').forEach(textarea => {
        if (!this.$validateField(textarea)) {
            isValid = false;
        }
    });

    // Validate all select elements
    form.querySelectorAll('select').forEach(select => {
        if (!this.$validateField(select)) {
            isValid = false;
        }
    });

    return isValid;
}
    /*End Form validations- for validateForm */


  	/*myGlobalFunction function*/
    app.config.globalProperties.$myGlobalFunction = () => {
    	//alert('This is a global function!');
      console.log('This is a global function!');
    };
    /*End myGlobalFunction function*/

    /*hasPermission function*/
    app.config.globalProperties.$hasPermission = function (perm) {
      const permissions = this.$store?.state?.permissions || [];
      return permissions.includes(perm);
    };
    /*End hasPermission function*/
    

    /*scrollToTop Arrorw function*/
    app.config.globalProperties.$scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    	/*$('html, body').animate({
		    scrollTop: 0
		}, 'smooth');*/ 
    };
    /*End scrollToTop Arrorw function*/
    /*scrollToTopModal Arrorw function*/
    app.config.globalProperties.$scrollToTopModal = (modalName) => {
      // main.js alertPosition = 'bottom': the alert shows at the bottom, so stay where you are
      if (app.config.globalProperties.$alertPosition === 'bottom') return;
      const modal = document.getElementById(modalName);
      if (modal) {
         modal.scrollTop = 0;
         // Scrollable forms: the fields part has its own scroll bar
         const body = modal.querySelector('.modal-body');
         if (body) body.scrollTop = 0;
      }
    	//$('#'+modalName).scrollTop(0);
    };
    /*End scrollToTopModal Arrorw function*/
    /*displayAlert normal function*/
    app.config.globalProperties.$displayAlert = function(alertstatus, alertMessage, alertType, pageOrModal) {
      // 'this' refers to the Vue component instance using this function
      const context = this;

      context.defaultAlert.pageOrModal = pageOrModal === 'modal' ? 'modal' : 'page';
      context.defaultAlert.alertMessage = ''; // Reset message first  
      context.defaultAlert.alertstatus = ''; // Reset status first

      context.$nextTick(() => {
        context.defaultAlert.alertstatus = alertstatus;
        context.defaultAlert.alertMessage = alertMessage;
        context.defaultAlert.alertType = alertType;
      });
    };
    /*End displayAlert function*/
    /*showOrHideLoader normal function (No context use but you can use)*/
    app.config.globalProperties.$showOrHideLoader = function(message) {
      console.log("MESSAGE:", message);
      console.log("THIS:", this);
      console.log("TABLE DATA:", this.tableData);
      this.$scrollToTop(); // ✅ works now, because 'this' is the Vue component instance
      this.tableData.loaderMessage = message;
      this.tableData.isVisible = !this.tableData.isVisible; // Toggle visibility
    };
    /*End showOrHideLoader normal function (No context use but you can use)*/


/*
  Location helpers with Promise support
*/

// Check initial location permission and redirect if needed
app.config.globalProperties.$checkLocationPermission = function (component, router, route) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {//check if the geolocation API is available in the browsers
      router.push('/location-required/');
      resolve();
      return;
    }

     /*navigator.permissions.query({ name: 'geolocation' }).then(function (result) {
      if (result.state === 'granted') {
        alert("Location is enabled and permission granted");
        // Location is enabled and permission granted
      } else if (result.state === 'prompt') {
        //router.push('/location-required/');
        alert("// Browser will ask the user for permission");
        // Browser will ask the user for permission
      } else if (result.state === 'denied') {
        alert("// Location permission denied");
        // Location permission denied
      }
    });*/

    const store = component.$store;
    //component.showAlert = true;
    //component.alertType = "success"; 
    //component.alertMessage = "Enabling location! Please wait...";
    component.progressMessage = "Enabling location! Please wait";
        

    //getCurrentPosition - Get executed only when user click Allow or Block
    navigator.geolocation.getCurrentPosition(
      (position) => {
        //alert("Congrats! Location is enabled");
        component.latitude = position.coords.latitude;
        component.longitude = position.coords.longitude;

        store.commit('setLatitude', position.coords.latitude);
        store.commit('setLongitude', position.coords.longitude);

        //component.showAlert = true;
        //component.alertType = "success"; 
        //component.alertMessage = "Location successfully enabled! Logging you in...";
        component.progressMessage = "Location enabled successfully! Please wait while we log you in";

        /*const intendedRoute = sessionStorage.getItem('intendedRoute');
        if (intendedRoute && route.fullPath !== intendedRoute) {
          sessionStorage.removeItem('intendedRoute');
          router.push(intendedRoute);
        }*/
        resolve();
      },
      (error) => {
        const fullMessage = app.config.globalProperties.$detectPlatform();
        component.loggingIn = false;
        component.showAlert = true;
        component.alertType = "danger"; 
        component.alertMessage = fullMessage;
        component.button_text = 'Verify Code';
  
        if (error.code === error.PERMISSION_DENIED && route.path !== '/location-required/') {
          sessionStorage.setItem('intendedRoute', route.fullPath);
          //router.push('/location-required/');
        }
        // Resolve even on error to let app continue
        resolve();
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
    //End getCurrentPosition - Get executed only when user click Allow or Block

  });
};

// Continuously watch location status
app.config.globalProperties.$watchLocationStatus = function (vm, router, route) {
  if (!navigator.geolocation) {
    router.push('/location-required/');
    return;
  }

  const store = component.$store;
  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      component.latitude = position.coords.latitude;
      component.longitude = position.coords.longitude;
      store.commit('setLatitude', position.coords.latitude);
      store.commit('setLongitude', position.coords.longitude);

      component.showAlert = true;
      component.alertType = "success"; 
      component.alertMessage = "Location successfully enabled! Logging you in...";

    },
    (error) => {
      const fullMessage = app.config.globalProperties.$detectPlatform();
      component.showAlert = true;
      component.alertType = "danger"; 
      component.alertMessage = fullMessage;

      /*if (error.code === error.PERMISSION_DENIED && route.path !== '/location-required/') {
        sessionStorage.setItem('intendedRoute', route.fullPath);
        router.push('/location-required/');
      }*/
    },
    {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    }
  );

  // Store the watch ID so you can stop watching later
  component._locationWatchId = watchId;
};

// Cleanup watcher when component is destroyed
app.mixin({
  beforeUnmount() {
    if (this._locationWatchId) {
      navigator.geolocation.clearWatch(this._locationWatchId);
    }
  }
});
/*
  End Location helpers with Promise support
*/

/* checkIfLocationIsEnabled function */
app.config.globalProperties.$checkIfCanFetchLocation = () => {
  const store = app.config.globalProperties.$store;
  const is_locationRequired = localStorage.getItem('is_locationRequired');

  // Offline: the location often can't be found without internet. Don't log out for that.
  if (!navigator.onLine) return;

  if (is_locationRequired == 1) {
    if (store.state.latitude === null) {
      //const fullMessage = app.config.globalProperties.$detectPlatform();
      const fullMessage = "Please log In again to enable location!";
      const alertType = "danger";
      app.config.globalProperties.$globalLogOut(alertType, fullMessage);
    } else {
      //Below or Nothing should happen
      //const fullMessage = "Location is enabled! Please log In again";
      //const alertType = "success";
    }
  }
};
/* End checkIfLocationIsEnabled function */

app.config.globalProperties.$detectPlatform = () => {
  const baseMessage = "Please enable location";

      // Detect platform and browser
      const ua = navigator.userAgent;
      let os = 'Unknown', browser = 'Unknown', platformInstruction = '';

      if (/iPad|iPhone|iPod/.test(ua)) os = 'iOS';
      else if (/Android/.test(ua)) os = 'Android';
      else os = 'Desktop';

      if (/Chrome/.test(ua) && !/Edge/.test(ua)) browser = 'Chrome';
      else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
      else if (/Firefox/.test(ua)) browser = 'Firefox';
      else if (/Edge/.test(ua)) browser = 'Edge';

      // Set platform-specific instruction
      if (os === 'iOS' && browser === 'Safari') {
        platformInstruction = "in your Safari browser! Go to Settings > Safari > Location and set it to 'While Using the App'. Or try to log in again if already enabled!";
      } else if (os === 'Android' && browser === 'Chrome') {
        platformInstruction = "in your Chrome browser! Go to Settings > Apps > Chrome > Permissions > Location and allow it. Or try to log in again if already enabled!";
      } else if (os === 'Android' && browser === 'Firefox') {
        platformInstruction = "in your Firefox browser! Go to Settings > Apps > Firefox > Permissions > Location and enable it. Or try to log in again if already enabled!";
      } else if (os === 'Desktop' && browser === 'Chrome') {
        platformInstruction = "in your Chrome browser! Click the padlock icon in the address bar, go to Site Settings, and enable Location. Or try to log in again if already enabled!";
      } else if (os === 'Desktop' && browser === 'Firefox') {
        platformInstruction = "in your Firefox browser! Click the shield icon in the address bar, go to Permissions, and allow Location. Or try to log in again if already enabled!";
      } else {
        platformInstruction = "Please check your browser or device settings to allow location access for this site. Or try to log in again if already enabled!";
      }

      const fullMessage = baseMessage + ' ' + platformInstruction;

      return fullMessage;
}


/* handleFormValidation method */
app.config.globalProperties.$handleFormValidation = function(formId) {
      const formIsValid = formValidations(formId); // Form validations

      if (!formIsValid) {
        this.handleAlert("starting", "Some fields are empty! Please fill", "danger", "modal");
        setTimeout(() => {
          this.handleAlert("ending", "Some fields are empty! Please fill", "danger", "modal");
        }, 1000);
        return false;
      }
      return true;
};
/* End handleFormValidation method */



// Single click: only highlight the row.
app.config.globalProperties.$onRowClick = function (record, event) {
    // Ignore clicks on the checkbox, buttons, links and the Actions menu.
    if (event.target.closest('input, button, a, .dropdown-menu')) return;

    this.tableData.selectedRecordId = record.id;
};

// Double click: open the edit form, same as the Edit button.
app.config.globalProperties.$onRowDblClick = function (record, event) {
    // Ignore double clicks on controls and on the expand cell.
    if (event.target.closest('input, button, a, .dropdown-menu, .expand-cell')) return;

    // Deleted records cannot be edited (same rule as the Edit button).
    if (record.is_deleted != 0) return;

    // Remove the word the browser selected during the double click.
    window.getSelection()?.removeAllRanges();

    this.$closeAllDetails();   // NEW

    this.editRecord(record);

const modalEl = document.querySelector('#recordFormModal');

bootstrap.Modal.getOrCreateInstance(modalEl).show();

};

// Close every open details row.
app.config.globalProperties.$closeAllDetails = function () {
    document.querySelectorAll('#bootstrapTable .table-details.show').forEach((open) => {
        bootstrap.Collapse.getOrCreateInstance(open).hide();
    });
    this.expandedRows = {};
};

// Enter on the table: expand the highlighted row, unless a modal is open.
app.config.globalProperties.$onTableEnter = function (event) {
    // Only when the table itself has focus, and no modal is showing.
    if (event.target !== event.currentTarget) return;
    if (document.querySelector('.modal.show')) return;

    this.$toggleDetails(this.tableData.selectedRecordId);
};

// Focus the first field of a form, on computers only (a mouse). Phones and tablets: no focus,
// so the on-screen keyboard doesn't pop up and cover the form.
app.config.globalProperties.$focusFirstField = function (modalEl) {
    // Gear panel → App Options → "Focus first field when a form opens (desktop)"
    if (!app.config.globalProperties.$focusFirstFieldOnDesktop) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    modalEl.querySelector('input:not([type="hidden"]), select, textarea')?.focus();
};

// Make Enter save a modal form, and focus its first field when it opens (computers only).
app.config.globalProperties.$enableModalEnterSave = function (modalId, formId) {
    const modalEl = document.querySelector(modalId);
    const form = document.querySelector(formId);
    if (!modalEl || !form) return;

    // Whenever the modal opens (Add, Edit or double click), focus the first field (computers only).
    modalEl.addEventListener('shown.bs.modal', () => {
        this.$focusFirstField(modalEl);
    });

    // While the modal is open, Enter saves the form from anywhere.
    this.modalEnterHandler = (event) => {
        if (event.key !== 'Enter' || !modalEl.classList.contains('show')) return;

        // Leave Enter alone where it has its own job inside the modal:
        // buttons, text areas, the file picker and an open dropdown.
        if (event.target.closest(
            `${modalId} button, textarea, input[type="file"], .vs--open`
        )) return;

        // NEW: ignore the repeats fired while Enter is held down.
        if (event.repeat) { event.preventDefault(); return; }

        event.preventDefault();
        form.requestSubmit();
    };
    document.addEventListener('keydown', this.modalEnterHandler);
};

// Stop listening for Enter (call when leaving the page).
app.config.globalProperties.$disableModalEnterSave = function () {
    document.removeEventListener('keydown', this.modalEnterHandler);
};

// Hide the "empty fields" alert once the form has become valid.
app.config.globalProperties.$clearValidationAlert = function (formId) {
    // Only after a failed save, and only for the "empty fields" alert.
    if (!this.submitted) return;
    if (this.defaultAlert.alertMessage !== "Fields Cannot be empty") return;

    // Wait for Vue to update the form, then check it again.
    this.$nextTick(() => {
        if (this.$validateForm(formId)) {
            this.defaultAlert.pageOrModal = "";   // hides the alert
        }
    });
};

// Keyboard shortcuts for the table page: Alt+N = add new, Alt+M = edit highlighted row.
app.config.globalProperties.$enableTableShortcuts = function () {
    this.tableShortcutHandler = (event) => {
        // No shortcuts while a form or confirm modal is open.
        if (document.querySelector('.modal.show')) return;

        // Both shortcuts use Alt alone, without Ctrl.
        if (!event.altKey || event.ctrlKey) return;

        // Alt+N: add a new record by pressing the page's Add button.
        if (event.code === 'KeyN') {
            event.preventDefault();

            // Start the new form clean: no old alert, no old validation marks.
            this.submitted = false;
            this.defaultAlert.pageOrModal = "";
            this.$clearValidationStates('#recordForm');

            document.querySelector('#addRecordBtn')?.click();
            return;
        }

        // Alt+M: edit the highlighted row.
        if (event.code === 'KeyM') {
            event.preventDefault();

            const record = this.tableData.records.find(
                (r) => r.id === this.tableData.selectedRecordId
            );

            // Nothing highlighted, or the record is deleted: do nothing.
            if (!record || record.is_deleted != 0) return;

            this.$closeAllDetails();
            this.editRecord(record);
            bootstrap.Modal.getOrCreateInstance(document.querySelector('#recordFormModal')).show();
        }
    };
    document.addEventListener('keydown', this.tableShortcutHandler);
};

// Stop listening for the shortcuts (call when leaving the page).
app.config.globalProperties.$disableTableShortcuts = function () {
    document.removeEventListener('keydown', this.tableShortcutHandler);
};


// Open or close the details row; only one row stays open at a time.
app.config.globalProperties.$toggleDetails = function (id) {
    const el = document.getElementById('tableDetails' + id);
    if (!el) return;

    // Close every other details row that is currently open.
    document.querySelectorAll('#bootstrapTable .table-details.show').forEach((open) => {
        if (open !== el) bootstrap.Collapse.getOrCreateInstance(open).hide();
    });

    // Remember only the clicked row as open (or none, if we are closing it).
    const willOpen = !el.classList.contains('show');
    this.expandedRows = willOpen ? { [id]: true } : {};

    bootstrap.Collapse.getOrCreateInstance(el).toggle();
};


// Arrow keys: move the highlight one row up (-1) or down (+1).
app.config.globalProperties.$moveRowSelection = function (step, event) {
    // Leave arrow keys alone inside the checkbox, buttons and the Actions menu.
    if (event.target.closest('input, button, a, .dropdown-menu')) return;

    // Stop the page itself from scrolling.
    event.preventDefault();

    const records = this.tableData.records;
    if (!records.length) return;

    // Find the current row, then step to the next one without leaving the list.
    const current = records.findIndex((r) => r.id === this.tableData.selectedRecordId);
    const next = Math.min(Math.max(current + step, 0), records.length - 1);

    this.tableData.selectedRecordId = records[next].id;

    // NEW: close any details row that is open.
    document.querySelectorAll('#bootstrapTable .table-details.show').forEach((open) => {
        bootstrap.Collapse.getOrCreateInstance(open).hide();
    });
    this.expandedRows = {};

    // Scroll the newly highlighted row into view if it is off screen.
    this.$nextTick(() => {
        document.querySelector('#bootstrapTable tr.active-row')
            ?.scrollIntoView({ block: 'nearest' });
    });
};


/*`handleConfig` function*/
app.config.globalProperties.$handleConfig = function(data = null, responseType = null) {
      const token = localStorage.getItem('jwt');
      const csrf_token = localStorage.getItem('csrf_token');
      let config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
          'X-CSRF-TOKEN': csrf_token // This is the commonly used header name
        },
        withCredentials: true
      };

      // Add responseType if provided
      if (responseType) {
        config.responseType = responseType;
      }

      // Only add data to the config if provided
      if (data) {
        config.data = data;
        // To change header content type if your header has data instead of in form
        config.headers['Content-Type'] = 'application/json';
      }

      return config;
    };
/*End `handleConfig` function*/

/*Add transformUnderscoreText as a global function*/
    app.config.globalProperties.$transformUnderscoreText = function(input) {
      if (!input) return '';
      return input
        .split('_') // Split by underscore
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
        .join(' '); // Join with space
    };
/*EndAdd transformUnderscoreText as a global function*/

/*Add formatDate function as a global method*/
    app.config.globalProperties.$formatDate = function(date) {
      const d = new Date(date);
      return d.toISOString().split('T')[0]; // Converts to YYYY-MM-DD format
    };
/*End Add formatDate function as a global method*/

/*ConvertToLocalDate*/
app.config.globalProperties.$ConvertToLocalDate = function(utcString) {
  if (!utcString) return '';  // If there's no date, return an empty string.

  // Convert UTC string to Date object
  const date = new Date(utcString + 'Z');  // Adding 'Z' to indicate UTC time

  // Convert the UTC time to the user's local time zone using toLocaleString
  const options = {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,  // Automatically uses user's local timezone
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,  // 24-hour format
    timeZoneName: 'short'
  };

  // Format and return the date as a localized string
  return date.toLocaleString('en-GB', options); // You can change 'en-GB' to the user's language/region
};
/*End ConvertToLocalDate*/


// Friendly date for tables: "Today, 5:22 PM", "Yesterday, 9:10 AM", "25 Oct, 5:22 PM", "3 Mar 2025, 8:00 AM".
// The value is a UTC date from the database ("2026-10-25 14:22:24"); shown in the user's own time.
app.config.globalProperties.$friendlyDate = function (value) {
   if (!value) return "-";

   const text = String(value);
   const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(text);
   const date = new Date(hasZone ? text : text.replace(" ", "T") + "Z");
   if (isNaN(date)) return value;

   const now = new Date();
   const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
   const daysAgo = Math.round((dayStart(now) - dayStart(date)) / 86400000);
   const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

   if (daysAgo === 0) return `Today, ${time}`;
   if (daysAgo === 1) return `Yesterday, ${time}`;

   const day = date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
   });
   return `${day}, ${time}`;
};

/*getFlag*/
app.config.globalProperties.$getFlag = function (code) {
   return [...code.toUpperCase()]
      .map(c => String.fromCodePoint(127397 + c.charCodeAt()))
      .join('');
};
/*End getFlag*/

/*getCountryTimezone*/
// Returns the timezone for a country code (e.g. "UG" -> "Africa/Kampala").
// If the device timezone belongs to that country, it is kept (useful for US, Russia, Australia...).
// Otherwise the country"s main timezone (first in the list) is used.
app.config.globalProperties.$getCountryTimezone = function (code, deviceTimezone = "") {
  const zones = {
    AD: ["Europe/Andorra"],
    AE: ["Asia/Dubai"],
    AF: ["Asia/Kabul"],
    AG: ["America/Antigua"],
    AI: ["America/Anguilla"],
    AL: ["Europe/Tirane"],
    AM: ["Asia/Yerevan"],
    AO: ["Africa/Luanda"],
    AQ: ["Antarctica/McMurdo", "Antarctica/Casey", "Antarctica/Davis", "Antarctica/DumontDUrville", "Antarctica/Mawson", "Antarctica/Palmer", "Antarctica/Rothera", "Antarctica/Syowa", "Antarctica/Troll", "Antarctica/Vostok"],
    AR: ["America/Argentina/Buenos_Aires", "America/Argentina/Cordoba", "America/Argentina/Salta", "America/Argentina/Jujuy", "America/Argentina/Tucuman", "America/Argentina/Catamarca", "America/Argentina/La_Rioja", "America/Argentina/San_Juan", "America/Argentina/Mendoza", "America/Argentina/San_Luis", "America/Argentina/Rio_Gallegos", "America/Argentina/Ushuaia"],
    AS: ["Pacific/Pago_Pago"],
    AT: ["Europe/Vienna"],
    AU: ["Australia/Sydney", "Australia/Lord_Howe", "Antarctica/Macquarie", "Australia/Hobart", "Australia/Melbourne", "Australia/Broken_Hill", "Australia/Brisbane", "Australia/Lindeman", "Australia/Adelaide", "Australia/Darwin", "Australia/Perth", "Australia/Eucla"],
    AW: ["America/Aruba"],
    AX: ["Europe/Mariehamn"],
    AZ: ["Asia/Baku"],
    BA: ["Europe/Sarajevo"],
    BB: ["America/Barbados"],
    BD: ["Asia/Dhaka"],
    BE: ["Europe/Brussels"],
    BF: ["Africa/Ouagadougou"],
    BG: ["Europe/Sofia"],
    BH: ["Asia/Bahrain"],
    BI: ["Africa/Bujumbura"],
    BJ: ["Africa/Porto-Novo"],
    BL: ["America/St_Barthelemy"],
    BM: ["Atlantic/Bermuda"],
    BN: ["Asia/Brunei"],
    BO: ["America/La_Paz"],
    BQ: ["America/Kralendijk"],
    BR: ["America/Sao_Paulo", "America/Noronha", "America/Belem", "America/Fortaleza", "America/Recife", "America/Araguaina", "America/Maceio", "America/Bahia", "America/Campo_Grande", "America/Cuiaba", "America/Santarem", "America/Porto_Velho", "America/Boa_Vista", "America/Manaus", "America/Eirunepe", "America/Rio_Branco"],
    BS: ["America/Nassau"],
    BT: ["Asia/Thimphu"],
    BW: ["Africa/Gaborone"],
    BY: ["Europe/Minsk"],
    BZ: ["America/Belize"],
    CA: ["America/Toronto", "America/St_Johns", "America/Halifax", "America/Glace_Bay", "America/Moncton", "America/Goose_Bay", "America/Blanc-Sablon", "America/Iqaluit", "America/Atikokan", "America/Winnipeg", "America/Resolute", "America/Rankin_Inlet", "America/Regina", "America/Swift_Current", "America/Edmonton", "America/Cambridge_Bay", "America/Inuvik", "America/Vancouver", "America/Creston", "America/Dawson_Creek", "America/Fort_Nelson", "America/Whitehorse", "America/Dawson"],
    CC: ["Indian/Cocos"],
    CD: ["Africa/Kinshasa", "Africa/Lubumbashi"],
    CF: ["Africa/Bangui"],
    CG: ["Africa/Brazzaville"],
    CH: ["Europe/Zurich"],
    CI: ["Africa/Abidjan"],
    CK: ["Pacific/Rarotonga"],
    CL: ["America/Santiago", "America/Coyhaique", "America/Punta_Arenas", "Pacific/Easter"],
    CM: ["Africa/Douala"],
    CN: ["Asia/Shanghai", "Asia/Urumqi"],
    CO: ["America/Bogota"],
    CR: ["America/Costa_Rica"],
    CU: ["America/Havana"],
    CV: ["Atlantic/Cape_Verde"],
    CW: ["America/Curacao"],
    CX: ["Indian/Christmas"],
    CY: ["Asia/Nicosia", "Asia/Famagusta"],
    CZ: ["Europe/Prague"],
    DE: ["Europe/Berlin", "Europe/Busingen"],
    DJ: ["Africa/Djibouti"],
    DK: ["Europe/Copenhagen"],
    DM: ["America/Dominica"],
    DO: ["America/Santo_Domingo"],
    DZ: ["Africa/Algiers"],
    EC: ["America/Guayaquil", "Pacific/Galapagos"],
    EE: ["Europe/Tallinn"],
    EG: ["Africa/Cairo"],
    EH: ["Africa/El_Aaiun"],
    ER: ["Africa/Asmara"],
    ES: ["Europe/Madrid", "Africa/Ceuta", "Atlantic/Canary"],
    ET: ["Africa/Addis_Ababa"],
    FI: ["Europe/Helsinki"],
    FJ: ["Pacific/Fiji"],
    FK: ["Atlantic/Stanley"],
    FM: ["Pacific/Pohnpei", "Pacific/Chuuk", "Pacific/Kosrae"],
    FO: ["Atlantic/Faroe"],
    FR: ["Europe/Paris"],
    GA: ["Africa/Libreville"],
    GB: ["Europe/London"],
    GD: ["America/Grenada"],
    GE: ["Asia/Tbilisi"],
    GF: ["America/Cayenne"],
    GG: ["Europe/Guernsey"],
    GH: ["Africa/Accra"],
    GI: ["Europe/Gibraltar"],
    GL: ["America/Nuuk", "America/Danmarkshavn", "America/Scoresbysund", "America/Thule"],
    GM: ["Africa/Banjul"],
    GN: ["Africa/Conakry"],
    GP: ["America/Guadeloupe"],
    GQ: ["Africa/Malabo"],
    GR: ["Europe/Athens"],
    GS: ["Atlantic/South_Georgia"],
    GT: ["America/Guatemala"],
    GU: ["Pacific/Guam"],
    GW: ["Africa/Bissau"],
    GY: ["America/Guyana"],
    HK: ["Asia/Hong_Kong"],
    HN: ["America/Tegucigalpa"],
    HR: ["Europe/Zagreb"],
    HT: ["America/Port-au-Prince"],
    HU: ["Europe/Budapest"],
    ID: ["Asia/Jakarta", "Asia/Pontianak", "Asia/Makassar", "Asia/Jayapura"],
    IE: ["Europe/Dublin"],
    IL: ["Asia/Jerusalem"],
    IM: ["Europe/Isle_of_Man"],
    IN: ["Asia/Kolkata"],
    IO: ["Indian/Chagos"],
    IQ: ["Asia/Baghdad"],
    IR: ["Asia/Tehran"],
    IS: ["Atlantic/Reykjavik"],
    IT: ["Europe/Rome"],
    JE: ["Europe/Jersey"],
    JM: ["America/Jamaica"],
    JO: ["Asia/Amman"],
    JP: ["Asia/Tokyo"],
    KE: ["Africa/Nairobi"],
    KG: ["Asia/Bishkek"],
    KH: ["Asia/Phnom_Penh"],
    KI: ["Pacific/Tarawa", "Pacific/Kanton", "Pacific/Kiritimati"],
    KM: ["Indian/Comoro"],
    KN: ["America/St_Kitts"],
    KP: ["Asia/Pyongyang"],
    KR: ["Asia/Seoul"],
    KW: ["Asia/Kuwait"],
    KY: ["America/Cayman"],
    KZ: ["Asia/Almaty", "Asia/Qyzylorda", "Asia/Qostanay", "Asia/Aqtobe", "Asia/Aqtau", "Asia/Atyrau", "Asia/Oral"],
    LA: ["Asia/Vientiane"],
    LB: ["Asia/Beirut"],
    LC: ["America/St_Lucia"],
    LI: ["Europe/Vaduz"],
    LK: ["Asia/Colombo"],
    LR: ["Africa/Monrovia"],
    LS: ["Africa/Maseru"],
    LT: ["Europe/Vilnius"],
    LU: ["Europe/Luxembourg"],
    LV: ["Europe/Riga"],
    LY: ["Africa/Tripoli"],
    MA: ["Africa/Casablanca"],
    MC: ["Europe/Monaco"],
    MD: ["Europe/Chisinau"],
    ME: ["Europe/Podgorica"],
    MF: ["America/Marigot"],
    MG: ["Indian/Antananarivo"],
    MH: ["Pacific/Majuro", "Pacific/Kwajalein"],
    MK: ["Europe/Skopje"],
    ML: ["Africa/Bamako"],
    MM: ["Asia/Yangon"],
    MN: ["Asia/Ulaanbaatar", "Asia/Hovd"],
    MO: ["Asia/Macau"],
    MP: ["Pacific/Saipan"],
    MQ: ["America/Martinique"],
    MR: ["Africa/Nouakchott"],
    MS: ["America/Montserrat"],
    MT: ["Europe/Malta"],
    MU: ["Indian/Mauritius"],
    MV: ["Indian/Maldives"],
    MW: ["Africa/Blantyre"],
    MX: ["America/Mexico_City", "America/Cancun", "America/Merida", "America/Monterrey", "America/Matamoros", "America/Chihuahua", "America/Ciudad_Juarez", "America/Ojinaga", "America/Mazatlan", "America/Bahia_Banderas", "America/Hermosillo", "America/Tijuana"],
    MY: ["Asia/Kuala_Lumpur", "Asia/Kuching"],
    MZ: ["Africa/Maputo"],
    NA: ["Africa/Windhoek"],
    NC: ["Pacific/Noumea"],
    NE: ["Africa/Niamey"],
    NF: ["Pacific/Norfolk"],
    NG: ["Africa/Lagos"],
    NI: ["America/Managua"],
    NL: ["Europe/Amsterdam"],
    NO: ["Europe/Oslo"],
    NP: ["Asia/Kathmandu"],
    NR: ["Pacific/Nauru"],
    NU: ["Pacific/Niue"],
    NZ: ["Pacific/Auckland", "Pacific/Chatham"],
    OM: ["Asia/Muscat"],
    PA: ["America/Panama"],
    PE: ["America/Lima"],
    PF: ["Pacific/Tahiti", "Pacific/Marquesas", "Pacific/Gambier"],
    PG: ["Pacific/Port_Moresby", "Pacific/Bougainville"],
    PH: ["Asia/Manila"],
    PK: ["Asia/Karachi"],
    PL: ["Europe/Warsaw"],
    PM: ["America/Miquelon"],
    PN: ["Pacific/Pitcairn"],
    PR: ["America/Puerto_Rico"],
    PS: ["Asia/Gaza", "Asia/Hebron"],
    PT: ["Europe/Lisbon", "Atlantic/Madeira", "Atlantic/Azores"],
    PW: ["Pacific/Palau"],
    PY: ["America/Asuncion"],
    QA: ["Asia/Qatar"],
    RE: ["Indian/Reunion"],
    RO: ["Europe/Bucharest"],
    RS: ["Europe/Belgrade"],
    RU: ["Europe/Moscow", "Europe/Kaliningrad", "Europe/Kirov", "Europe/Volgograd", "Europe/Astrakhan", "Europe/Saratov", "Europe/Ulyanovsk", "Europe/Samara", "Asia/Yekaterinburg", "Asia/Omsk", "Asia/Novosibirsk", "Asia/Barnaul", "Asia/Tomsk", "Asia/Novokuznetsk", "Asia/Krasnoyarsk", "Asia/Irkutsk", "Asia/Chita", "Asia/Yakutsk", "Asia/Khandyga", "Asia/Vladivostok", "Asia/Ust-Nera", "Asia/Magadan", "Asia/Sakhalin", "Asia/Srednekolymsk", "Asia/Kamchatka", "Asia/Anadyr"],
    RW: ["Africa/Kigali"],
    SA: ["Asia/Riyadh"],
    SB: ["Pacific/Guadalcanal"],
    SC: ["Indian/Mahe"],
    SD: ["Africa/Khartoum"],
    SE: ["Europe/Stockholm"],
    SG: ["Asia/Singapore"],
    SH: ["Atlantic/St_Helena"],
    SI: ["Europe/Ljubljana"],
    SJ: ["Arctic/Longyearbyen"],
    SK: ["Europe/Bratislava"],
    SL: ["Africa/Freetown"],
    SM: ["Europe/San_Marino"],
    SN: ["Africa/Dakar"],
    SO: ["Africa/Mogadishu"],
    SR: ["America/Paramaribo"],
    SS: ["Africa/Juba"],
    ST: ["Africa/Sao_Tome"],
    SV: ["America/El_Salvador"],
    SX: ["America/Lower_Princes"],
    SY: ["Asia/Damascus"],
    SZ: ["Africa/Mbabane"],
    TC: ["America/Grand_Turk"],
    TD: ["Africa/Ndjamena"],
    TF: ["Indian/Kerguelen"],
    TG: ["Africa/Lome"],
    TH: ["Asia/Bangkok"],
    TJ: ["Asia/Dushanbe"],
    TK: ["Pacific/Fakaofo"],
    TL: ["Asia/Dili"],
    TM: ["Asia/Ashgabat"],
    TN: ["Africa/Tunis"],
    TO: ["Pacific/Tongatapu"],
    TR: ["Europe/Istanbul"],
    TT: ["America/Port_of_Spain"],
    TV: ["Pacific/Funafuti"],
    TW: ["Asia/Taipei"],
    TZ: ["Africa/Dar_es_Salaam"],
    UA: ["Europe/Kyiv", "Europe/Simferopol"],
    UG: ["Africa/Kampala"],
    UM: ["Pacific/Wake", "Pacific/Midway"],
    US: ["America/New_York", "America/Detroit", "America/Kentucky/Louisville", "America/Kentucky/Monticello", "America/Indiana/Indianapolis", "America/Indiana/Vincennes", "America/Indiana/Winamac", "America/Indiana/Marengo", "America/Indiana/Petersburg", "America/Indiana/Vevay", "America/Chicago", "America/Indiana/Tell_City", "America/Indiana/Knox", "America/Menominee", "America/North_Dakota/Center", "America/North_Dakota/New_Salem", "America/North_Dakota/Beulah", "America/Denver", "America/Boise", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "America/Juneau", "America/Sitka", "America/Metlakatla", "America/Yakutat", "America/Nome", "America/Adak", "Pacific/Honolulu"],
    UY: ["America/Montevideo"],
    UZ: ["Asia/Tashkent", "Asia/Samarkand"],
    VA: ["Europe/Vatican"],
    VC: ["America/St_Vincent"],
    VE: ["America/Caracas"],
    VG: ["America/Tortola"],
    VI: ["America/St_Thomas"],
    VN: ["Asia/Ho_Chi_Minh"],
    VU: ["Pacific/Efate"],
    WF: ["Pacific/Wallis"],
    WS: ["Pacific/Apia"],
    YE: ["Asia/Aden"],
    YT: ["Indian/Mayotte"],
    ZA: ["Africa/Johannesburg"],
    ZM: ["Africa/Lusaka"],
    ZW: ["Africa/Harare"],
  };
  const list = zones[String(code || "").toUpperCase()];
  if (!list || !list.length) return deviceTimezone || "Africa/Nairobi";
  return list.includes(deviceTimezone) ? deviceTimezone : list[0];
};
/*End getCountryTimezone*/

/*getCountryName: "KE" -> "Kenya" (from $getCountries). Unknown values are shown as they are.*/
app.config.globalProperties.$getCountryName = function (code) {
   const value = String(code || '').trim().toUpperCase();
   const country = this.$getCountries().find(c => c.code === value);
   return country ? country.name : (code || '-');
};
/*End getCountryName*/

/*getCountries*/
app.config.globalProperties.$getCountries = function () {
   return [
      { name: "Afghanistan", code: "AF", currency: "AFN" },
      { name: "Albania", code: "AL", currency: "ALL" },
      { name: "Algeria", code: "DZ", currency: "DZD" },
      { name: "Andorra", code: "AD", currency: "EUR" },
      { name: "Angola", code: "AO", currency: "AOA" },
      { name: "Antigua and Barbuda", code: "AG", currency: "XCD" },
      { name: "Argentina", code: "AR", currency: "ARS" },
      { name: "Armenia", code: "AM", currency: "AMD" },
      { name: "Australia", code: "AU", currency: "AUD" },
      { name: "Austria", code: "AT", currency: "EUR" },
      { name: "Azerbaijan", code: "AZ", currency: "AZN" },
      { name: "Bahamas", code: "BS", currency: "BSD" },
      { name: "Bahrain", code: "BH", currency: "BHD" },
      { name: "Bangladesh", code: "BD", currency: "BDT" },
      { name: "Barbados", code: "BB", currency: "BBD" },
      { name: "Belarus", code: "BY", currency: "BYN" },
      { name: "Belgium", code: "BE", currency: "EUR" },
      { name: "Belize", code: "BZ", currency: "BZD" },
      { name: "Benin", code: "BJ", currency: "XOF" },
      { name: "Bhutan", code: "BT", currency: "BTN" },
      { name: "Bolivia", code: "BO", currency: "BOB" },
      { name: "Bosnia and Herzegovina", code: "BA", currency: "BAM" },
      { name: "Botswana", code: "BW", currency: "BWP" },
      { name: "Brazil", code: "BR", currency: "BRL" },
      { name: "Brunei", code: "BN", currency: "BND" },
      { name: "Bulgaria", code: "BG", currency: "EUR" },
      { name: "Burkina Faso", code: "BF", currency: "XOF" },
      { name: "Burundi", code: "BI", currency: "BIF" },
      { name: "Cambodia", code: "KH", currency: "KHR" },
      { name: "Cameroon", code: "CM", currency: "XAF" },
      { name: "Canada", code: "CA", currency: "CAD" },
      { name: "Cape Verde", code: "CV", currency: "CVE" },
      { name: "Central African Republic", code: "CF", currency: "XAF" },
      { name: "Chad", code: "TD", currency: "XAF" },
      { name: "Chile", code: "CL", currency: "CLP" },
      { name: "China", code: "CN", currency: "CNY" },
      { name: "Colombia", code: "CO", currency: "COP" },
      { name: "Comoros", code: "KM", currency: "KMF" },
      { name: "Congo", code: "CG", currency: "XAF" },
      { name: "Costa Rica", code: "CR", currency: "CRC" },
      { name: "Croatia", code: "HR", currency: "EUR" },
      { name: "Cuba", code: "CU", currency: "CUP" },
      { name: "Cyprus", code: "CY", currency: "EUR" },
      { name: "Czech Republic", code: "CZ", currency: "CZK" },
      { name: "Denmark", code: "DK", currency: "DKK" },
      { name: "Djibouti", code: "DJ", currency: "DJF" },
      { name: "Dominica", code: "DM", currency: "XCD" },
      { name: "Dominican Republic", code: "DO", currency: "DOP" },
      { name: "Ecuador", code: "EC", currency: "USD" },
      { name: "Egypt", code: "EG", currency: "EGP" },
      { name: "El Salvador", code: "SV", currency: "USD" },
      { name: "Equatorial Guinea", code: "GQ", currency: "XAF" },
      { name: "Eritrea", code: "ER", currency: "ERN" },
      { name: "Estonia", code: "EE", currency: "EUR" },
      { name: "Eswatini", code: "SZ", currency: "SZL" },
      { name: "Ethiopia", code: "ET", currency: "ETB" },
      { name: "Fiji", code: "FJ", currency: "FJD" },
      { name: "Finland", code: "FI", currency: "EUR" },
      { name: "France", code: "FR", currency: "EUR" },
      { name: "Gabon", code: "GA", currency: "XAF" },
      { name: "Gambia", code: "GM", currency: "GMD" },
      { name: "Georgia", code: "GE", currency: "GEL" },
      { name: "Germany", code: "DE", currency: "EUR" },
      { name: "Ghana", code: "GH", currency: "GHS" },
      { name: "Greece", code: "GR", currency: "EUR" },
      { name: "Grenada", code: "GD", currency: "XCD" },
      { name: "Guatemala", code: "GT", currency: "GTQ" },
      { name: "Guinea", code: "GN", currency: "GNF" },
      { name: "Guinea-Bissau", code: "GW", currency: "XOF" },
      { name: "Guyana", code: "GY", currency: "GYD" },
      { name: "Haiti", code: "HT", currency: "HTG" },
      { name: "Honduras", code: "HN", currency: "HNL" },
      { name: "Hungary", code: "HU", currency: "HUF" },
      { name: "Iceland", code: "IS", currency: "ISK" },
      { name: "India", code: "IN", currency: "INR" },
      { name: "Indonesia", code: "ID", currency: "IDR" },
      { name: "Iran", code: "IR", currency: "IRR" },
      { name: "Iraq", code: "IQ", currency: "IQD" },
      { name: "Ireland", code: "IE", currency: "EUR" },
      { name: "Israel", code: "IL", currency: "ILS" },
      { name: "Italy", code: "IT", currency: "EUR" },
      { name: "Jamaica", code: "JM", currency: "JMD" },
      { name: "Japan", code: "JP", currency: "JPY" },
      { name: "Jordan", code: "JO", currency: "JOD" },
      { name: "Kazakhstan", code: "KZ", currency: "KZT" },
      { name: "Kenya", code: "KE", currency: "KES" },
      { name: "Kiribati", code: "KI", currency: "AUD" },
      { name: "Kuwait", code: "KW", currency: "KWD" },
      { name: "Kyrgyzstan", code: "KG", currency: "KGS" },
      { name: "Laos", code: "LA", currency: "LAK" },
      { name: "Latvia", code: "LV", currency: "EUR" },
      { name: "Lebanon", code: "LB", currency: "LBP" },
      { name: "Lesotho", code: "LS", currency: "LSL" },
      { name: "Liberia", code: "LR", currency: "LRD" },
      { name: "Libya", code: "LY", currency: "LYD" },
      { name: "Liechtenstein", code: "LI", currency: "CHF" },
      { name: "Lithuania", code: "LT", currency: "EUR" },
      { name: "Luxembourg", code: "LU", currency: "EUR" },
      { name: "Madagascar", code: "MG", currency: "MGA" },
      { name: "Malawi", code: "MW", currency: "MWK" },
      { name: "Malaysia", code: "MY", currency: "MYR" },
      { name: "Maldives", code: "MV", currency: "MVR" },
      { name: "Mali", code: "ML", currency: "XOF" },
      { name: "Malta", code: "MT", currency: "EUR" },
      { name: "Marshall Islands", code: "MH", currency: "USD" },
      { name: "Mauritania", code: "MR", currency: "MRU" },
      { name: "Mauritius", code: "MU", currency: "MUR" },
      { name: "Mexico", code: "MX", currency: "MXN" },
      { name: "Micronesia", code: "FM", currency: "USD" },
      { name: "Moldova", code: "MD", currency: "MDL" },
      { name: "Monaco", code: "MC", currency: "EUR" },
      { name: "Mongolia", code: "MN", currency: "MNT" },
      { name: "Montenegro", code: "ME", currency: "EUR" },
      { name: "Morocco", code: "MA", currency: "MAD" },
      { name: "Mozambique", code: "MZ", currency: "MZN" },
      { name: "Myanmar", code: "MM", currency: "MMK" },
      { name: "Namibia", code: "NA", currency: "NAD" },
      { name: "Nauru", code: "NR", currency: "AUD" },
      { name: "Nepal", code: "NP", currency: "NPR" },
      { name: "Netherlands", code: "NL", currency: "EUR" },
      { name: "New Zealand", code: "NZ", currency: "NZD" },
      { name: "Nicaragua", code: "NI", currency: "NIO" },
      { name: "Niger", code: "NE", currency: "XOF" },
      { name: "Nigeria", code: "NG", currency: "NGN" },
      { name: "North Korea", code: "KP", currency: "KPW" },
      { name: "North Macedonia", code: "MK", currency: "MKD" },
      { name: "Norway", code: "NO", currency: "NOK" },
      { name: "Oman", code: "OM", currency: "OMR" },
      { name: "Pakistan", code: "PK", currency: "PKR" },
      { name: "Palau", code: "PW", currency: "USD" },
      { name: "Panama", code: "PA", currency: "PAB" },
      { name: "Papua New Guinea", code: "PG", currency: "PGK" },
      { name: "Paraguay", code: "PY", currency: "PYG" },
      { name: "Peru", code: "PE", currency: "PEN" },
      { name: "Philippines", code: "PH", currency: "PHP" },
      { name: "Poland", code: "PL", currency: "PLN" },
      { name: "Portugal", code: "PT", currency: "EUR" },
      { name: "Qatar", code: "QA", currency: "QAR" },
      { name: "Romania", code: "RO", currency: "RON" },
      { name: "Russia", code: "RU", currency: "RUB" },
      { name: "Rwanda", code: "RW", currency: "RWF" },
      { name: "Saint Kitts and Nevis", code: "KN", currency: "XCD" },
      { name: "Saint Lucia", code: "LC", currency: "XCD" },
      { name: "Saint Vincent and the Grenadines", code: "VC", currency: "XCD" },
      { name: "Samoa", code: "WS", currency: "WST" },
      { name: "San Marino", code: "SM", currency: "EUR" },
      { name: "Sao Tome and Principe", code: "ST", currency: "STN" },
      { name: "Saudi Arabia", code: "SA", currency: "SAR" },
      { name: "Senegal", code: "SN", currency: "XOF" },
      { name: "Serbia", code: "RS", currency: "RSD" },
      { name: "Seychelles", code: "SC", currency: "SCR" },
      { name: "Sierra Leone", code: "SL", currency: "SLE" },
      { name: "Singapore", code: "SG", currency: "SGD" },
      { name: "Slovakia", code: "SK", currency: "EUR" },
      { name: "Slovenia", code: "SI", currency: "EUR" },
      { name: "Solomon Islands", code: "SB", currency: "SBD" },
      { name: "Somalia", code: "SO", currency: "SOS" },
      { name: "South Africa", code: "ZA", currency: "ZAR" },
      { name: "South Korea", code: "KR", currency: "KRW" },
      { name: "South Sudan", code: "SS", currency: "SSP" },
      { name: "Spain", code: "ES", currency: "EUR" },
      { name: "Sri Lanka", code: "LK", currency: "LKR" },
      { name: "Sudan", code: "SD", currency: "SDG" },
      { name: "Suriname", code: "SR", currency: "SRD" },
      { name: "Sweden", code: "SE", currency: "SEK" },
      { name: "Switzerland", code: "CH", currency: "CHF" },
      { name: "Syria", code: "SY", currency: "SYP" },
      { name: "Taiwan", code: "TW", currency: "TWD" },
      { name: "Tajikistan", code: "TJ", currency: "TJS" },
      { name: "Tanzania", code: "TZ", currency: "TZS" },
      { name: "Thailand", code: "TH", currency: "THB" },
      { name: "Timor-Leste", code: "TL", currency: "USD" },
      { name: "Togo", code: "TG", currency: "XOF" },
      { name: "Tonga", code: "TO", currency: "TOP" },
      { name: "Trinidad and Tobago", code: "TT", currency: "TTD" },
      { name: "Tunisia", code: "TN", currency: "TND" },
      { name: "Turkey", code: "TR", currency: "TRY" },
      { name: "Turkmenistan", code: "TM", currency: "TMT" },
      { name: "Tuvalu", code: "TV", currency: "AUD" },
      { name: "Uganda", code: "UG", currency: "UGX" },
      { name: "Ukraine", code: "UA", currency: "UAH" },
      { name: "United Arab Emirates", code: "AE", currency: "AED" },
      { name: "United Kingdom", code: "GB", currency: "GBP" },
      { name: "United States", code: "US", currency: "USD" },
      { name: "Uruguay", code: "UY", currency: "UYU" },
      { name: "Uzbekistan", code: "UZ", currency: "UZS" },
      { name: "Vanuatu", code: "VU", currency: "VUV" },
      { name: "Vatican City", code: "VA", currency: "EUR" },
      { name: "Venezuela", code: "VE", currency: "VES" },
      { name: "Vietnam", code: "VN", currency: "VND" },
      { name: "Yemen", code: "YE", currency: "YER" },
      { name: "Zambia", code: "ZM", currency: "ZMW" },
      { name: "Zimbabwe", code: "ZW", currency: "ZWG" }
   ];
};
/*End getCountries*/



/* ================= globalLogOut function ================= */
app.config.globalProperties.$globalLogOut = function(
  alertType,
  message = "You have been logged out successfully!!!"
) {
  this.isVisible = true;

  const token = localStorage.getItem('jwt');
  const csrf_token = localStorage.getItem('csrf_token');

  // If authentication tokens are missing,
  // perform a local logout instead.
  if (!token || !csrf_token) {
    this.$forceLogOut(alertType, message);
    return;
  }

  const config = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-CSRF-TOKEN': csrf_token
    },
    withCredentials: true
  };

  const formData = new FormData();
  formData.append("action", "logout");

  axios.post(`/auth/api_auth.php`, formData, config)
    .then(() => {

      this.$forceLogOut(alertType, message);

    })
    .catch(error => {

      console.error('Logout error:', error);

      // Even if backend logout fails,
      // clear the local session and redirect.
      this.$forceLogOut(
        "danger",
        "You have been logged out."
      );
    });
};
/* ================= End globalLogOut function ================= */

/* ================= forceLogOut function ================= */
app.config.globalProperties.$forceLogOut = function(
  alertType = "danger",
  message = "Your session has expired. Please log in again."
) {

  // Hide loader
  this.isVisible = false;

  // Hide logout modal if it is open
  this.$hideModal('logOutModal');

  // Clear Vuex state
  this.$store.commit('SET_TOKEN_EXPIRATION', 0);
  this.$store.commit('SET_LOGGED_IN', false);

  // Clear authentication information
  localStorage.removeItem('jwt');
  localStorage.removeItem('csrf_token');
  localStorage.removeItem('is_locationRequired');
  localStorage.removeItem('loggedIn');
  localStorage.removeItem('company_currency'); // the next person gets their own company's currency
  sessionStorage.removeItem('apps_welcome_shown'); // the "Add apps" welcome may show again at the next login
    // Remove the saved offline tables, so the next person on this device sees nothing
  clearOfflineData();

  // Store message for login page
  sessionStorage.setItem(
    'backend_message',
    message
  );

  sessionStorage.setItem(
    'backend_alertType',
    alertType
  );

  // Redirect to login
  this.$router.push('/');
};
/* ================= End forceLogOut function ================= */


  }
};
